/**
 * Notifications, on the client.
 *
 * One query holds the list and one socket handler keeps it current, so the
 * bell, the peek panel and the notifications screen all read the same thing
 * and cannot disagree about how many are waiting.
 *
 * The count is carried on the socket event rather than recomputed from the
 * list. The list is capped and paged; the count is not, and a badge derived
 * from a page of the list would quietly stop counting past fifty.
 */

import { useEffect } from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api, socket } from './api';
import type { PublicUser } from './types';

export type NotificationKind =
  | 'mention' | 'dm' | 'groupMessage'
  | 'friendRequest' | 'friendAccepted'
  | 'partyInvite' | 'partyJoined' | 'partyKicked' | 'partyReady' | 'partyOpened' | 'threadReply'
  | 'tournament' | 'tournamentMatch' | 'tournamentDispute'
  | 'crewRequest' | 'crewAccepted' | 'crewRole' | 'crewRemoved' | 'crewInvite' | 'crewEvent'
  | 'badge' | 'levelUp' | 'announcement' | 'system';

export type NotificationGroup = 'chat' | 'social' | 'party' | 'crew' | 'you' | 'system';

export type Notification = {
  id: string;
  kind: NotificationKind;
  group: NotificationGroup;
  /** An icon name, chosen on the server so every client draws it the same. */
  icon: string;
  title: string;
  body: string;
  /** Where clicking it should land, as a hash path. */
  route: string | null;
  meta: Record<string, unknown> | null;
  createdAt: number;
  readAt: number | null;
  read: boolean;
  actor: PublicUser | null;
};

export type NotificationPage = {
  notifications: Notification[];
  unread: number;
  groups: NotificationGroup[];
};

export const NOTIFICATIONS_KEY = ['notifications'] as const;

/** The list and the unread total. Shared by everything that shows either. */
export function useNotifications() {
  return useQuery({
    queryKey: NOTIFICATIONS_KEY,
    queryFn: () => api.get<NotificationPage>('/api/notifications'),
    staleTime: 30_000
  });
}

/** Just the number, for the bell. */
export function useUnreadNotifications(): number {
  const q = useNotifications();
  return q.data?.unread ?? 0;
}

/**
 * Keeps the cache current from the socket.
 *
 * Mounted once, in the shell. Writes the arriving notification straight into
 * the cached page rather than refetching: a notification is already the whole
 * row, and asking the server to send fifty of them back to learn about one is
 * the sort of thing that makes an app feel heavy under load.
 */
export function useNotificationFeed(onArrive?: (n: Notification) => void): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    const offs = [
      socket.on('notification', (raw: unknown) => {
        const d = raw as { notification: Notification; unread: number };
        if (!d?.notification) return;

        queryClient.setQueryData<NotificationPage>(NOTIFICATIONS_KEY, (old) => {
          const list = old?.notifications ?? [];
          /* The server coalesces repeat messages from one conversation onto
             one row, so an arrival can be an update rather than an addition. */
          const without = list.filter((n) => n.id !== d.notification.id);
          return {
            notifications: [d.notification, ...without].slice(0, 50),
            unread: d.unread,
            groups: old?.groups ?? []
          };
        });

        onArrive?.(d.notification);
      }),

      socket.on('notification:count', (raw: unknown) => {
        const d = raw as { unread: number };
        queryClient.setQueryData<NotificationPage>(NOTIFICATIONS_KEY, (old) =>
          old ? { ...old, unread: d.unread } : old);
      })
    ];
    return () => { for (const off of offs) off(); };
  }, [queryClient, onArrive]);
}

/* ----------------------------------------------------------- actions */

/**
 * Marks read, on screen first.
 *
 * Reading a notification is not a thing you wait for. The badge drops
 * immediately and the request follows; if it fails the next fetch puts the
 * truth back, which is the right trade for something this small.
 */
export async function markRead(qc: QueryClient, ids?: string[]): Promise<void> {
  qc.setQueryData<NotificationPage>(NOTIFICATIONS_KEY, (old) => {
    if (!old) return old;
    const hit = (n: Notification) => !ids || ids.includes(n.id);
    const next = old.notifications.map((n) =>
      hit(n) && !n.read ? { ...n, read: true, readAt: Date.now() } : n);
    return { ...old, notifications: next, unread: next.filter((n) => !n.read).length };
  });

  try {
    await api.post('/api/notifications/read', ids ? { ids } : {});
  } catch {
    void qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
  }
}

/** Removes one, or all of them when no id is given. */
export async function clearNotifications(qc: QueryClient, id?: string): Promise<void> {
  qc.setQueryData<NotificationPage>(NOTIFICATIONS_KEY, (old) => {
    if (!old) return old;
    const next = id ? old.notifications.filter((n) => n.id !== id) : [];
    return { ...old, notifications: next, unread: next.filter((n) => !n.read).length };
  });

  try {
    await api.del(id ? '/api/notifications/' + id : '/api/notifications');
  } catch {
    void qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
  }
}

/** Short relative time, for a list where everything is recent. */
export function ago(at: number): string {
  const s = Math.max(0, (Date.now() - at) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return Math.floor(s / 60) + 'm';
  if (s < 86400) return Math.floor(s / 3600) + 'h';
  if (s < 604800) return Math.floor(s / 86400) + 'd';
  return new Date(at).toLocaleDateString();
}
